import { Check } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { cn } from "@/lib/utils";

const planStyles = {
  emerald: {
    badge: "bg-emerald-500/15 text-emerald-100 border border-emerald-500/20",
    ring: "ring-emerald-500/20",
    bar: "from-emerald-500/70 to-emerald-400/70",
    check: "text-emerald-400",
  },
  orange: {
    badge: "bg-amber-500/15 text-amber-100 border border-amber-500/25",
    ring: "ring-amber-500/25",
    bar: "from-amber-500/70 to-orange-400/70",
    check: "text-amber-400",
  },
  blue: {
    badge: "bg-sky-500/15 text-sky-100 border border-sky-500/25",
    ring: "ring-sky-500/25",
    bar: "from-sky-500/70 to-blue-400/70",
    check: "text-sky-400",
  },
  magenta: {
    badge: "bg-fuchsia-500/15 text-fuchsia-100 border border-fuchsia-500/25",
    ring: "ring-fuchsia-500/25",
    bar: "from-fuchsia-500/70 to-pink-400/70",
    check: "text-fuchsia-400",
  },
  red: {
    badge: "bg-rose-500/15 text-rose-100 border border-rose-500/25",
    ring: "ring-rose-500/25",
    bar: "from-rose-500/70 to-red-400/70",
    check: "text-rose-400",
  },
};

type PlanStyleKey = keyof typeof planStyles;

type Plan = {
  id: string;
  name: string;
  price: string;
  cadence?: string;
  description: string;
  color: PlanStyleKey;
  cta: string;
  features: string[];
};

const plans: Plan[] = [];

export function PlansSection() {
  return (
    <section className="mb-10 space-y-6 rounded-3xl border border-border/60 bg-card/50 p-6 shadow-lg backdrop-blur">
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Plans</p>
          <h2 className="text-2xl font-display font-bold">Updated color-coded tiers</h2>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Each plan now carries a distinct accent: Free Trial is green, Starter is orange, then blue, magenta, and red for the
 higher tiers.
          </p>
        </div>
      </div>

      {plans.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border/60 bg-card/40 px-6 py-10 text-center text-sm text-muted-foreground">
          No plans are configured yet.
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {plans.map(plan => {
            const style = planStyles[plan.color];
            return (
              <Card
                key={plan.id}
                className={cn(
                  "relative overflow-hidden border border-border/60 bg-card/70 backdrop-blur ring-1 transition hover:-translate-y-1 hover:shadow-2xl",
                  style.ring
                )}
              >
                <div className={cn("absolute inset-x-0 top-0 h-1 bg-gradient-to-r", style.bar)} />
                <CardHeader className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Badge className={cn("rounded-full px-3 py-1 text-xs", style.badge)}>{plan.name}</Badge>
                    <span className="text-xs text-muted-foreground">{plan.cadence}</span>
                  </div>
                  <CardTitle className="text-2xl font-semibold">{plan.price}</CardTitle>
                  <CardDescription className="leading-relaxed">{plan.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    {plan.features.map(feature => (
                      <li key={feature} className="flex items-start gap-2">
                        <Check className={cn("mt-0.5 h-4 w-4", style.check)} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button className="w-full" variant="secondary">
                    {plan.cta}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </section>
  );
}
